/**
 * MermaidDiagram
 *
 * Lazy-loaded Mermaid renderer. The mermaid library (~500KB) is fetched
 * via dynamic import only when this component mounts, so the docs page is
 * the only place that pays for the bundle.
 *
 * Dark-mode reactive: subscribes to changes on document.documentElement
 * and re-renders with the matching Mermaid theme.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@respectly/ui';

export interface MermaidDiagramProps {
  chart: string;
  className?: string;
  caption?: string;
}

function getCurrentTheme(): 'default' | 'dark' {
  if (typeof document === 'undefined') return 'default';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'default';
}

export function MermaidDiagram({ chart, className, caption }: MermaidDiagramProps) {
  const reactId = useId();
  const safeId = `mermaid-${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<'default' | 'dark'>(getCurrentTheme);

  // Watch for dark-mode class changes on <html>.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const next = root.classList.contains('dark') ? 'dark' : 'default';
      setTheme((prev) => (prev === next ? prev : next));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Render (or re-render) when chart or theme changes.
  useEffect(() => {
    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const mermaidModule = await import('mermaid');
        const mermaid = mermaidModule.default;
        mermaid.initialize({
          startOnLoad: false,
          theme,
          securityLevel: 'strict',
          fontFamily: 'inherit',
          flowchart: { htmlLabels: true, curve: 'basis' },
          themeVariables:
            theme === 'dark'
              ? {
                  background: 'transparent',
                  primaryColor: '#1e293b',
                  primaryTextColor: '#f1f5f9',
                  primaryBorderColor: '#475569',
                  lineColor: '#94a3b8',
                  secondaryColor: '#334155',
                  tertiaryColor: '#0f172a',
                }
              : {
                  background: 'transparent',
                  primaryColor: '#f8fafc',
                  primaryTextColor: '#0f172a',
                  primaryBorderColor: '#cbd5e1',
                  lineColor: '#64748b',
                  secondaryColor: '#e2e8f0',
                  tertiaryColor: '#f1f5f9',
                },
        });

        const { svg: rendered } = await mermaid.render(safeId, chart);
        if (!cancelled) setSvg(rendered);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chart, theme, safeId]);

  return (
    <figure
      className={cn(
        'my-6 rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40 p-4 md:p-6 overflow-x-auto',
        className
      )}
    >
      {error ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Diagram failed to render: {error}
        </div>
      ) : (
        <div
          ref={containerRef}
          className="mermaid-render flex justify-center [&_svg]:max-w-full [&_svg]:h-auto"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
      {caption ? (
        <figcaption className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
